import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Plus, Trash2, Pencil, Building2, Image as ImageIcon, Languages } from 'lucide-react'
import PitWizard from './PitWizard'
import { selectCls, labelCls } from './WizardModal'
import { useConfirm, deleteJSON } from './ConfirmDialog'
import { ErrorState } from './ViewState'
import { oreTypeLabel, pitTypeLabel, pitStatusLabel } from '../equipment'
import { useWS } from '../contexts/WSContext'
import { cleanTextMap, pickText } from '../lib/i18nText'

// Nome do pit nos idiomas da cabine (VMC 0.8): o texto no idioma da UI, quando difere do
// nome canônico, e a lista dos idiomas preenchidos; o tooltip traz todos.
function PitNames({ pit }) {
  const { t, i18n } = useTranslation()
  const names = cleanTextMap(pit.names)
  const langs = Object.keys(names)
  if (langs.length === 0) return null
  const localized = pickText(names, i18n.language, pit.name)
  const tip = `${t('admin:pits.translations')}\n${langs.map(l => `${l.toUpperCase()}: ${names[l]}`).join('\n')}`
  return (
    <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500 min-w-0" title={tip}>
      <Languages size={12} className="shrink-0" />
      {localized !== pit.name && <span className="truncate text-slate-500 dark:text-slate-400">{localized}</span>}
      <span className="shrink-0 font-mono">{langs.map(l => l.toUpperCase()).join(' ')}</span>
    </p>
  )
}

// Administração › Pits: todos os pits das minas em um só lugar, com filtro por mina.
// Os dados vêm de mines[].pits (agregado do backend, recarregado pelo reloadAdmin).
export default function AdminPits() {
  const { t } = useTranslation()
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const { mines, adminError, reloadAdmin } = useWS()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [mineFilter, setMineFilter] = useState('')
  const [confirmDialog, ask] = useConfirm()

  const pits = mines.flatMap(m => (Array.isArray(m.pits) ? m.pits : []).map(p => ({ ...p, mine: m })))
  const shown = mineFilter ? pits.filter(p => String(p.mine.id) === mineFilter) : pits

  const openCreate = () => { setEditing(null); setOpen(true) }
  const openEdit = (p) => { setEditing({ ...p, mine_id: p.mine.id }); setOpen(true) }
  const remove = (p) => ask({
    title: t('admin:pits.delete'),
    message: t('admin:pits.confirm_delete', { name: p.name }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/pits/${p.id}`); reloadAdmin() },
  })

  const equipCount = (p) => (Array.isArray(p.equipment_ids) ? p.equipment_ids.length : 0)

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-slate-500 dark:text-slate-400 pb-2">{t('admin:pits.count', { count: shown.length })}</p>
        <div className="flex items-end gap-3">
          <label className="block w-56">
            <span className={labelCls}>{t('admin:pits.filter_mine')}</span>
            <select value={mineFilter} onChange={e => setMineFilter(e.target.value)} className={selectCls}>
              <option value="">{t('admin:pits.all_mines')}</option>
              {mines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
            <Plus size={16} /> {t('admin:pits.add')}
          </button>
        </div>
      </div>

      {adminError ? (
        <ErrorState message={adminError} onRetry={reloadAdmin} />
      ) : shown.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="w-full flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={28} />
          <span className="text-sm">{t('admin:pits.empty_cta')}</span>
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map(p => (
            <div key={p.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-md transition-shadow">
              {p.image_url
                ? <img src={p.image_url} alt="" className="w-full h-24 object-cover" />
                : <div className="w-full h-24 bg-slate-200 dark:bg-slate-700 flex items-center justify-center"><ImageIcon size={20} className="text-slate-400" /></div>}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{p.name}</p>
                    <PitNames pit={p} />
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 truncate">
                      <Building2 size={12} className="text-slate-400 dark:text-slate-500 shrink-0" />
                      <span className="truncate">{[p.mine.name, oreTypeLabel(p.ore_type ?? p.ore), pitTypeLabel(p.type)].filter(Boolean).join(' · ')}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button disabled={!mayEdit} onClick={() => openEdit(p)} title={!mayEdit ? t(reasonEdit) : t('admin:pits.edit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={15} /></button>
                    <button disabled={!mayDelete} onClick={() => remove(p)} title={!mayDelete ? t(reasonDelete) : t('admin:pits.delete')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  {p.status && <span className="px-1.5 py-0.5 rounded text-[11px] bg-brand/10 text-brand-700 dark:text-brand-300 font-medium">{pitStatusLabel(p.status)}</span>}
                  <span className="ml-auto text-[11px] text-slate-400 dark:text-slate-500">{t('admin:mines.equip_count', { count: equipCount(p) })}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PitWizard key={`pit-${editing?.id ?? 'new'}-${open}`} open={open} onClose={() => setOpen(false)} editing={editing} mineId={mineFilter ? Number(mineFilter) : null} />
      {confirmDialog}
    </div>
  )
}
